import { Capacitor } from '@capacitor/core';
import { isNativeApp } from './platform';
import { generateInstallId, isValidInstallId } from './installIdCore';

/**
 * Storage for the per-install identifier (see installIdCore.ts).
 *
 * Preferred store is @capacitor/preferences (SharedPreferences / UserDefaults —
 * survives WebView storage eviction on iOS); it ships with the 1.0.13 shells.
 * Older shells have no such plugin and fall back to localStorage. When the
 * plugin appears after an update, the localStorage value is adopted so the
 * device keeps its identity (and its cap). Returns null on the web: browsers
 * never send an install id, their cap stays per IP.
 */
const KEY = 'snugtale_install_id';

function readLocal(): string | null {
  try {
    const v = localStorage.getItem(KEY);
    return isValidInstallId(v) ? v : null;
  } catch {
    return null;
  }
}

function writeLocal(id: string): void {
  try { localStorage.setItem(KEY, id); } catch { /* ignore */ }
}

export async function getOrCreateInstallId(): Promise<string | null> {
  if (!isNativeApp()) return null;
  const local = readLocal();
  if (Capacitor.isPluginAvailable('Preferences')) {
    try {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key: KEY });
      const id = isValidInstallId(value) ? value : (local ?? generateInstallId());
      if (value !== id) await Preferences.set({ key: KEY, value: id });
      if (local !== id) writeLocal(id);
      return id;
    } catch { /* plugin misbehaved — fall back to localStorage below */ }
  }
  const id = local ?? generateInstallId();
  if (local !== id) writeLocal(id);
  return id;
}
